import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useCurrentUser, userHasGroup, userIsAbrechnung, userIsBuero, userIsRealAppAdmin } from "@/lib/auth";
import { formatCurrency } from "@/lib/documentCalc";
import { ApiError } from "@/lib/apiClient";
import { useRealtime } from "@/lib/useRealtime";
import { toast } from "@/lib/toast";
import {
  canBillingReopenDokument,
  deleteDokument,
  DOKUMENT_STATUS_BADGE,
  DOKUMENT_STATUS_LABELS,
  getDokumentTypLabel,
  getDokumentPdfUrl,
  formatBelegdatum,
  isStuecklisteListItem,
  listDokumente,
  type DokumentListItem,
} from "@/lib/dokumente";
import { deleteStueckliste } from "@/lib/stuecklisten";
import {
  formatMahnungDate,
  listMahnungen,
  mahnungStatusLabel,
  type MahnungListItem,
} from "@/lib/mahnwesen";
import { BillingReopenDialog } from "@/pages/dokumente/BillingReopenDialog";
import { useDocumentCreate } from "@/pages/dokumente/useDocumentCreate";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import {
  collapseAppDocumentRevisions,
  isNativeAppOverviewDocument,
} from "@/projectOverview/appDocumentsDisplay";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";

interface ProjectAppDocumentsSectionProps {
  auftragId: number;
}

function fmtDateTime(value: string | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("de-DE");
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="eh-project-doc-meta-row">
      <dt className="eh-project-doc-meta-row__label">{label}</dt>
      <dd className="eh-project-doc-meta-row__value">{value || "—"}</dd>
    </div>
  );
}

function beauftragungLabel(item: DokumentListItem): string {
  if (item.ist_beauftragt) return "Beauftragtes Angebot";
  if (item.dokument_typ === "angebot" && item.status === "abgelehnt") return "Abgelehnt";
  if (item.dokument_typ === "angebot" && item.status === "angenommen") return "Angenommen";
  if (item.dokument_typ === "angebot") return "Noch nicht beauftragt";
  return "—";
}

function AppDocumentCard({
  item,
  onOpen,
  onBillingReopen,
  onDeleteDraft,
  canBillingReopen,
  deleting,
}: {
  item: DokumentListItem;
  onOpen: () => void;
  onBillingReopen?: () => void;
  onDeleteDraft?: () => void;
  canBillingReopen?: boolean;
  deleting?: boolean;
}) {
  const title = item.dokument_nummer || "Entwurf";
  const pdfUrl = getDokumentPdfUrl(item.id);
  const showKalkulation = item.dokument_typ === "angebot" && (item.kalkulation_stunden?.length ?? 0) > 0;

  return (
    <article className="eh-project-doc-card rounded-ios-lg border border-separator bg-card">
      <header className="eh-project-doc-card__header">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onOpen}
            className="text-base font-semibold text-accent hover:underline"
          >
            {title}
          </button>
          <p className="mt-1 text-sm text-label-secondary">
            {[getDokumentTypLabel(item), item.betreff].filter(Boolean).join(" · ") || "—"}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-start justify-end gap-2">
          {!item.dokument_nummer ? (
            <span className="rounded-full bg-status-orange/15 px-2.5 py-0.5 text-xs font-semibold text-status-orange">
              Entwurf
            </span>
          ) : null}
          {item.ist_beauftragt ? (
            <span className="rounded-full bg-status-green/15 px-2.5 py-0.5 text-xs font-semibold text-status-green">
              Beauftragt
            </span>
          ) : null}
          <span
            className={clsx(
              "rounded-full px-2.5 py-0.5 text-xs font-semibold",
              DOKUMENT_STATUS_BADGE[item.status],
            )}
          >
            {DOKUMENT_STATUS_LABELS[item.status]}
          </span>
        </div>
      </header>

      <dl className="eh-project-doc-card__meta">
        <MetaRow label="Erstellt" value={fmtDateTime(item.created_at)} />
        <MetaRow label="Ersteller" value={String(item.created_by_name || item.created_by || "—")} />
        <MetaRow label="Belegdatum" value={formatBelegdatum(item.belegdatum)} />
        <MetaRow label="Gültig bis" value={formatBelegdatum(item.gueltig_bis)} />
        <MetaRow label="Betrag (brutto)" value={formatCurrency(item.summe_brutto, item.waehrung)} />
        <MetaRow label="Betrag (netto)" value={formatCurrency(item.summe_netto, item.waehrung)} />
        <MetaRow label="Dokumenttyp" value={getDokumentTypLabel(item)} />
        <MetaRow label="Status" value={DOKUMENT_STATUS_LABELS[item.status]} />
        {item.dokument_typ === "angebot" ? (
          <MetaRow label="Beauftragung" value={beauftragungLabel(item)} />
        ) : null}
        <MetaRow label="App-Dokument-ID" value={String(item.id)} />
      </dl>

      {showKalkulation ? (
        <div className="border-t border-separator px-4 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            Kalkulierte Stunden nach Stundenart
          </p>
          <ul className="space-y-1 text-sm">
            {item.kalkulation_stunden?.map((zeile) => (
              <li key={zeile.lohnart_id} className="flex justify-between gap-3">
                <span className="text-label-secondary">{zeile.bezeichnung}</span>
                <span className="font-medium tabular-nums text-label">
                  {zeile.stunden.toLocaleString("de-DE", { maximumFractionDigits: 2 })} h
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <footer className="eh-project-doc-card__footer flex flex-wrap gap-3">
        <button type="button" onClick={onOpen} className="text-sm font-medium text-accent hover:underline">
          In App bearbeiten
        </button>
        {canBillingReopen && onBillingReopen ? (
          <button
            type="button"
            onClick={onBillingReopen}
            className="text-sm font-medium text-accent hover:underline"
          >
            Als Teil-/Vorkasse markieren
          </button>
        ) : null}
        {item.status === "entwurf" && onDeleteDraft ? (
          <button
            type="button"
            onClick={onDeleteDraft}
            disabled={deleting}
            className="text-sm font-medium text-status-red hover:underline disabled:opacity-50"
          >
            Entwurf löschen
          </button>
        ) : null}
        {item.status !== "storniert" && item.status !== "entwurf" ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-accent hover:underline"
          >
            PDF öffnen
          </a>
        ) : null}
        {item.sharepoint_web_url ? (
          <a
            href={item.sharepoint_web_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-accent hover:underline"
          >
            In SharePoint öffnen
          </a>
        ) : null}
      </footer>
    </article>
  );
}

export function ProjectAppDocumentsSection({ auftragId }: ProjectAppDocumentsSectionProps) {
  const { user } = useCurrentUser();
  const isBuero = Boolean(user && userIsBuero(user));
  const canDeleteDrafts = Boolean(
    user &&
      (userIsBuero(user) ||
        userIsAbrechnung(user) ||
        userIsRealAppAdmin(user) ||
        userHasGroup(user, "App-Admins")),
  );
  const { open: openDocumentCreate } = useDocumentCreate();
  const { open: openDocumentEditor } = useDocumentEditor();
  const { open: openProjectOverview } = useProjectOverview();
  const navigate = useNavigate();
  const [items, setItems] = useState<DokumentListItem[]>([]);
  const [mahnungen, setMahnungen] = useState<MahnungListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [billingSubject, setBillingSubject] = useState<{
    id: number;
    label: string;
    auftragId: number | null;
    auftragNummer?: string | null;
    currentArt?: string | null;
  } | null>(null);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const [list, mahnungenListe] = await Promise.all([
        listDokumente({ auftrag_id: auftragId }),
        listMahnungen({ auftrag_id: auftragId, limit: 100 }).catch(() => [] as MahnungListItem[]),
      ]);
      // Hero-PDFs (auch aktivierte Spiegel) und Stundennachweise haben eigene Bereiche.
      setItems((list || []).filter(isNativeAppOverviewDocument));
      setMahnungen(mahnungenListe);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "App-Dokumente konnten nicht geladen werden.");
      if (!options?.silent) setItems([]);
    } finally {
      setLoading(false);
    }
  }, [auftragId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime-Änderungen still einpflegen – die Liste bleibt dabei sichtbar.
  useRealtime({
    scopes: [`auftraege:${auftragId}`, "dokumente:list"],
    domains: ["dokumente", "hero", "auftraege"],
    events: ["dokumente_changed", "dokument_status_changed", "hero_changed", "auftraege_changed"],
    enabled: !!auftragId,
    onChange: () => {
      void reload({ silent: true });
    },
  });

  async function handleDeleteDraft(item: DokumentListItem) {
    const label = String(item.display_number || item.dokument_nummer || "Entwurf").trim();
    if (
      !window.confirm(
        `Entwurf „${label}“ löschen? Nur dieses Dokument wird entfernt, das Projekt bleibt erhalten.`,
      )
    ) {
      return;
    }
    setDeletingId(item.id);
    setError(null);
    try {
      if (isStuecklisteListItem(item)) {
        const stuecklisteId = Number(item.stueckliste_id || 0);
        if (!stuecklisteId) throw new Error("Stückliste ohne ID.");
        await deleteStueckliste(stuecklisteId);
      } else {
        await deleteDokument(item.id);
      }
      toast.success("Entwurf gelöscht.");
      await reload({ silent: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  }

  const visibleItems = collapseAppDocumentRevisions(items);
  const stats = {
    total: visibleItems.length,
    angebote: visibleItems.filter((item) => item.doc.dokument_typ === "angebot").length,
    beauftragt: visibleItems.filter((item) => item.doc.ist_beauftragt).length,
    summe: visibleItems.reduce((sum, item) => sum + Number(item.doc.summe_brutto || 0), 0),
  };

  return (
    <section className="space-y-3 rounded-ios-lg border border-separator bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-label">App-Dokumente</h3>
          <p className="text-sm text-label-secondary">
            Angebote, Rechnungen und Lieferscheine aus dem Dokumentenbereich.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => openDocumentCreate({ auftragId, typ: "angebot" })}
        >
          Neues Dokument
        </Button>
      </div>

      {error ? <Alert variant="error">{error}</Alert> : null}

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-label-secondary">
          <Spinner />
          App-Dokumente werden geladen …
        </div>
      ) : items.length === 0 && mahnungen.length === 0 ? (
        <p className="text-sm text-label-secondary">Für dieses Projekt gibt es noch keine App-Dokumente.</p>
      ) : (
        <>
          <div className="eh-project-doc-stats grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
              <p className="text-xs text-label-tertiary">Dokumente</p>
              <p className="text-lg font-semibold tabular-nums text-label">{stats.total}</p>
            </div>
            <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
              <p className="text-xs text-label-tertiary">Angebote</p>
              <p className="text-lg font-semibold tabular-nums text-label">{stats.angebote}</p>
            </div>
            <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
              <p className="text-xs text-label-tertiary">Beauftragt</p>
              <p className="text-lg font-semibold tabular-nums text-label">{stats.beauftragt}</p>
            </div>
            <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
              <p className="text-xs text-label-tertiary">Summe Beträge</p>
              <p className="text-lg font-semibold tabular-nums text-label">
                {formatCurrency(stats.summe, items[0]?.waehrung || "EUR")}
              </p>
            </div>
          </div>

          {mahnungen.length ? (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-label">Mahnungen</h4>
              <ul className="space-y-1">
                {mahnungen.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      className="text-left text-sm font-medium text-accent hover:underline"
                      onClick={() => navigate(`/dokumente/mahnwesen/${m.id}`)}
                    >
                      {m.nummer || m.stufe_bezeichnung}
                      {" · "}
                      {m.stufe_bezeichnung}
                      {" · "}
                      {mahnungStatusLabel(m.status, true)}
                      {" · "}
                      {formatMahnungDate(m.belegdatum)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="space-y-3">
            {visibleItems.map(({ doc: item }) => (
              <AppDocumentCard
                key={`${item.quelle || "gd"}-${item.stueckliste_id ?? item.id}`}
                item={item}
                onOpen={() => {
                  if (item.quelle === "stueckliste" && item.auftrag_id) {
                    openProjectOverview(item.auftrag_id, {
                      openMontageunterlagen: true,
                      stuecklisteId: item.stueckliste_id || undefined,
                    });
                    return;
                  }
                  openDocumentEditor({ docId: item.id });
                }}
                canBillingReopen={isBuero && canBillingReopenDokument(item)}
                deleting={deletingId === item.id}
                onDeleteDraft={
                  canDeleteDrafts && item.status === "entwurf"
                    ? () => void handleDeleteDraft(item)
                    : undefined
                }
                onBillingReopen={() => {
                  setBillingSubject({
                    id: item.id,
                    label: item.dokument_nummer || "Rechnung",
                    auftragId: item.auftrag_id,
                    auftragNummer: item.auftrag_nummer,
                    currentArt: item.rechnung_art,
                  });
                }}
              />
            ))}
          </div>
        </>
      )}

      <BillingReopenDialog
        open={billingSubject != null}
        subject={
          billingSubject
            ? {
                source: "gd",
                id: billingSubject.id,
                label: billingSubject.label,
                auftragId: billingSubject.auftragId,
                auftragNummer: billingSubject.auftragNummer,
                currentArt: billingSubject.currentArt,
              }
            : null
        }
        onClose={() => setBillingSubject(null)}
        onDone={() => {
          setBillingSubject(null);
          void reload({ silent: true });
        }}
      />
    </section>
  );
}
